"use strict";
//
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const core_1 = __importStar(require("@hcengineering/core"));
const _1 = __importDefault(require("."));
function isCreateTx(tx) {
    return tx._class === core_1.default.class.TxCreateDoc;
}
function isUpdateTx(tx) {
    return tx._class === core_1.default.class.TxUpdateDoc;
}
function isMixinTx(tx) {
    return tx._class === core_1.default.class.TxMixin;
}
function isPersonTx(tx) {
    return tx.objectClass === _1.default.class.Person;
}
function isSocialIdentityTx(tx) {
    return tx.objectClass === _1.default.class.SocialIdentity;
}
class ContactCache {
    static _instance;
    /**
     * [PersonId (socialId _id) => Ref<Person>] cache
     */
    _personIdsByPersonRef = new Map();
    /**
     * [PersonId (socialId _id) => Ref<Person>] cache
     */
    _personRefByPersonId = new Map();
    /**
     * [PersonId (socialId _id) => Person] cache
     */
    _personByPersonId = new Map();
    /**
     * [Ref<Person> => Person] cache
     */
    _personByRef = new Map();
    /**
     * [PersonId (socialId _id) => SocialIdentity] cache
     */
    _socialIdByPersonId = new Map();
    _changeListeners = [];
    constructor() {
        // Private constructor to prevent direct instantiation
    }
    static get instance() {
        this._instance ??= new ContactCache();
        return this._instance;
    }
    get personRefByPersonId() {
        return this._personRefByPersonId;
    }
    get personByPersonId() {
        return this._personByPersonId;
        // Alternatively, can build from two other maps but might be not as performant
        // return new Map(this._personRefByPersonId.entries().map(([personId, personRef]) => [personId, personRef != null ? this._personByRef.get(personRef) ?? null : null]))
    }
    get personByRef() {
        return this._personByRef;
    }
    get socialIdByPersonId() {
        return this._socialIdByPersonId;
    }
    addPersonIdToPersonRef(personRef, personId) {
        this._personIdsByPersonRef.set(personRef, (this._personIdsByPersonRef.get(personRef) ?? new Set()).add(personId));
    }
    fillCachesForPersonId(personId, socialId) {
        if (socialId == null) {
            // Shouldn't normally be the case, if the social id with the given id is not found at all, it's weird from where the id came from.
            // So might be some garbage data in the database, so just set nulls to skip it.
            this._personRefByPersonId.set(personId, null);
            this._personByPersonId.set(personId, null);
            this._socialIdByPersonId.set(personId, null);
            return;
        }
        const person = socialId.$lookup?.attachedTo;
        this._personRefByPersonId.set(personId, socialId.attachedTo);
        this.addPersonIdToPersonRef(socialId.attachedTo, personId);
        this._personByPersonId.set(personId, person ?? null);
        this._socialIdByPersonId.set(personId, socialId);
        if (person != null) {
            this._personByRef.set(person._id, person);
        }
    }
    fillCachesForPersonRef(personRef, person) {
        this._personByRef.set(personRef, person ?? null);
        for (const sidObj of (person?.$lookup?.socialIds ?? [])) {
            this._personRefByPersonId.set(sidObj._id, personRef);
            this.addPersonIdToPersonRef(personRef, sidObj._id);
            this._personByPersonId.set(sidObj._id, person ?? null);
        }
    }
    shouldHandleCreateTx(tx) {
        return isCreateTx(tx) && (isPersonTx(tx) || isSocialIdentityTx(tx));
    }
    shouldHandleUpdateOrMixinTx(tx) {
        return (isUpdateTx(tx) || isMixinTx(tx)) && isPersonTx(tx);
    }
    handleTx = (txes) => {
        for (const tx of txes) {
            if (this.shouldHandleCreateTx(tx)) {
                if (isPersonTx(tx)) {
                    this.handleCreatePersonTx(tx);
                }
                else if (isSocialIdentityTx(tx)) {
                    this.handleCreateSocialIdentityTx(tx);
                }
            }
            else if (this.shouldHandleUpdateOrMixinTx(tx)) {
                this.handleUpdateOrMixinPersonTx(tx);
            }
        }
    };
    handleCreatePersonTx(tx) {
        const ref = tx.objectId;
        const person = this._personByRef.get(ref);
        if (person === undefined)
            return;
        const createdPerson = core_1.TxProcessor.createDoc2Doc(tx);
        this._personByRef.set(ref, createdPerson);
        const personIds = Array.from(this._personIdsByPersonRef.get(ref) ?? []);
        for (const personId of personIds) {
            this._personByPersonId.set(personId, createdPerson);
        }
        this.broadcastChange({
            personRef: ref,
            personIds
        });
    }
    handleCreateSocialIdentityTx(tx) {
        const personId = tx.objectId;
        const personRef = this._personRefByPersonId.get(personId);
        if (personRef === undefined)
            return;
        const newPersonRef = tx.attachedTo;
        this._personRefByPersonId.set(personId, newPersonRef);
        this.addPersonIdToPersonRef(newPersonRef, personId);
        const createdSocialId = core_1.TxProcessor.createDoc2Doc(tx);
        this._socialIdByPersonId.set(personId, createdSocialId);
        this.broadcastChange({
            personRef: newPersonRef,
            personIds: [personId]
        });
    }
    handleUpdateOrMixinPersonTx(tx) {
        const ref = tx.objectId;
        const person = this._personByRef.get(ref);
        if (person == null)
            return;
        const updatedPerson = isUpdateTx(tx)
            ? core_1.TxProcessor.updateDoc2Doc(person, tx)
            : core_1.TxProcessor.updateMixin4Doc(person, tx);
        this._personByRef.set(ref, updatedPerson);
        const personIds = Array.from(this._personIdsByPersonRef.get(ref) ?? []);
        for (const personId of personIds) {
            this._personByPersonId.set(personId, updatedPerson);
        }
        this.broadcastChange({
            personRef: ref,
            personIds
        });
    }
    addChangeListener(listener) {
        this._changeListeners.push(listener);
    }
    removeChangeListener(listener) {
        const pos = this._changeListeners.findIndex((it) => it === listener);
        if (pos !== -1) {
            this._changeListeners.splice(pos, 1);
        }
    }
    broadcastChange(change) {
        for (const listener of this._changeListeners) {
            void listener(change);
        }
    }
}
exports.default = ContactCache;
//# sourceMappingURL=cache.js.map